/**
 * THE SPINE — the ordered list an owner writes, and the step graph it compiles to.
 *
 * PURE. No I/O, no clock, no Vue, no store. Two functions and their small vocabulary, so the
 * one genuinely new algorithm in the redesign can be walked in a spec without mounting
 * anything.
 *
 *   spineToDefinition(spine)      → TaskDefinition   (the wire shape `taskDefinitionSchema` parses)
 *   definitionToSpine(definition) → { spine, unreadable, reasons }
 *
 * ── WHAT THE LIST MEANS ───────────────────────────────────────────────────────────────
 * A conversation is linear. A task is a conversation. So the owner writes a LIST and this
 * file writes the graph: `firstStepId` is the first row, and a row's onward link is simply
 * the row under it. Everything that is not "carry on down the list" is an OUTCOME the owner
 * chose on purpose, and is carried on the row where she chose it:
 *
 *   { go: 'on' }                  carry on to the row below  (the default; writes the id itself)
 *   { go: 'step', step: 'day' }   go to that row             (further down, or back up)
 *   { go: 'stop' }                the task stops here        (no onward link is written)
 *   { go: 'handOver' }            hand the chat to a person
 *
 * Which wire field an outcome lands in is this file's business and nobody else's:
 *   • the onward link is `next` on every type except `call`, where it is `onWorked`;
 *   • a `choose` option's own outcome is that option's `next`;
 *   • a `call`'s "if it failed" is `onFailed` — and `handOver` writes NOTHING, because a
 *     call with no `onFailed` is exactly what makes the engine hand the chat over
 *     (`decide.ts`: "No `onFailed` means the owner never said what to do when this breaks");
 *   • a live list's "if nothing came back" is `ifNone`, which the schema makes REQUIRED, so
 *     `handOver` there mints a real hand-over row rather than writing nothing.
 *
 * ── WHY `clears` IS THE HEART OF THIS FILE ────────────────────────────────────────────
 * `clears` fires ON ARRIVAL, never on departure, and it is the ONLY way a conversation goes
 * backwards: the walk skips every step whose answer is already saved, so a jump back to an
 * earlier question lands on the very answer that sent the customer down the branch and walks
 * straight forward again. A loop with no `clears` is inert. (`services/ai-sidecar/src/tasks/
 * decide.ts` — `walk`, and the `StepBase.clears` docblock in `types.ts`.)
 *
 * So the owner never writes `clears`. She says "if the calendar has nothing, ask *Which day*
 * again", and THIS is the rule that turns that into a set of answers to forget. For a jump
 * back from row O to row T it forgets:
 *
 *   (a) T's own answer — without it the loop is inert, and this is the whole mechanism; plus
 *       every LATER answer that was computed from it, followed through connection inputs.
 *       "Which time" is fetched with the day, so forgetting the day must forget the time, or
 *       the walk skips the time step and books the slot the customer just rejected. A `call`'s
 *       `keep` keys are deliberately NOT followed: a call is never skipped, so it re-runs and
 *       rewrites them by itself.
 *   (b) the answer of the step whose own CHOICE routes into O — otherwise the walk arrives at
 *       that step, finds it answered, takes the same branch again and comes straight back to
 *       O. Measured against the live booking task: without it, "change the day" loops until
 *       `MAX_CLEARS_PER_WALK` gives up and hands the chat over.
 *
 * And when the backward jump is a row's own onward link — a "that day is full" message that
 * then goes back up — the SAME set is written on that row too, because arriving there is
 * where the loop really begins.
 *
 * The rule is a proposal, never a cage: `row.clears` may hold an explicit list, and then that
 * list is written verbatim. That is what keeps a hand-written graph byte-identical through a
 * round trip — see `specs/task-spine-roundtrip.spec.js`, which proves it against the real
 * sixteen-step booking task running on dev.
 *
 * ── WHAT THE LIST CANNOT SAY ──────────────────────────────────────────────────────────
 * `definitionToSpine` REFUSES rather than guesses. Every refusal names a shape the list has
 * nowhere to put, never a shape that is merely invalid — a link to a step somebody deleted,
 * a step nothing reaches, a `clears` entry naming an answer no step writes are all carried
 * through untouched, because `localProblems` already reports them and `TaskStepLink.vue`
 * already draws a target that is no longer there. A silent lossy round trip would rewrite a
 * live booking flow the first time its owner pressed Done; an honest refusal opens the map.
 */
import { saveKeyOf, savedKeysOf, targetOf } from './task-flow-draft.js';

/** Where a row's onward link is written. Every type but `call` carries it as `next`. */
const ONWARD_FIELD = { call: 'onWorked' };

const onwardFieldFor = type => ONWARD_FIELD[type] ?? 'next';

/** Nothing runs after these, so the list never writes an onward link on them. */
const FINAL_TYPES = new Set(['handoff', 'end']);

/** The types that collect an answer — the only ones the walk can skip, so the only ones `clears` can put back. */
const ANSWERING_TYPES = new Set(['ask', 'choose', 'choose_live']);

/** Fields this file owns. They are stripped off the row body and written back from outcomes. */
const OWNED_BY_TYPE = {
  ask: ['next', 'clears'],
  choose: ['next', 'clears', 'options'],
  choose_live: ['next', 'clears', 'ifNone'],
  call: ['next', 'clears', 'onWorked', 'onFailed'],
  tell: ['next', 'clears'],
  handoff: ['next', 'clears'],
  end: ['next', 'clears'],
};

// ── outcomes ──────────────────────────────────────────────────────────────────────────

/** Carry on to the row below. */
export const CARRY_ON = Object.freeze({ go: 'on' });
/** The task stops here. */
export const STOP = Object.freeze({ go: 'stop' });
/** Hand the chat to a person. */
export const HAND_OVER = Object.freeze({ go: 'handOver' });
/** Go to a named row — further down the list, or back up it. */
export const goToStep = step => ({ go: 'step', step });

/** The name a minted hand-over row is given, and the note the person picking it up reads. */
export const HAND_OVER_NAME = 'Hand over';
export const handOverNoteFor = name =>
  `${name || 'A step of this task'} had nothing to offer the customer, so the chat needs a person.`;

// ── small, total readers ──────────────────────────────────────────────────────────────

const isObject = value => typeof value === 'object' && value !== null;

const stepsOf = definition =>
  isObject(definition) && Array.isArray(definition.steps)
    ? definition.steps
    : [];

const idOf = row =>
  isObject(row) && isObject(row.step) ? row.step.id : undefined;

const typeOf = row =>
  isObject(row) && isObject(row.step) ? row.step.type : undefined;

/** The answer a row collects, in the sidecar's own spelling. `''` for a row that collects nothing. */
const answerKeyOf = row => {
  const target = targetOf(row.step);
  return target ? saveKeyOf(target) : '';
};

/** The chat keys a row hands to a connection. The only way one answer can depend on another. */
const inputKeysOf = row => {
  const step = row.step;
  if (step.type !== 'call' && step.type !== 'choose_live') return [];
  if (!isObject(step.inputs)) return [];
  return Object.values(step.inputs)
    .filter(
      source =>
        isObject(source) && typeof source.key === 'string' && source.key !== ''
    )
    .map(source => source.key);
};

/** A copy of the step with the fields this file owns taken off it. Everything else rides along. */
const bodyOf = step => {
  const owned = new Set(OWNED_BY_TYPE[step?.type] ?? ['next', 'clears']);
  const body = {};
  Object.keys(step ?? {}).forEach(key => {
    if (!owned.has(key)) body[key] = step[key];
  });
  return body;
};

/** A copy of one option with its own onward link taken off it. */
const optionBodyOf = option => {
  const body = {};
  Object.keys(option ?? {}).forEach(key => {
    if (key !== 'next') body[key] = option[key];
  });
  return body;
};

/**
 * The row id an outcome lands on, or `undefined` when it writes nothing.
 *
 * "Carry on" never lands on a MINTED hand-over row (P-2.2): those are appended after the rows
 * the owner wrote, so the last row she wrote carrying on would otherwise hand every finished
 * chat to a person. Carrying on past her last row ends the task.
 */
const resolveOnward = (then, index, rows) => {
  const go = isObject(then) ? then.go : 'on';
  if (go === 'step')
    return typeof then.step === 'string' ? then.step : undefined;
  if (go === 'stop' || go === 'handOver') return undefined;
  if (FINAL_TYPES.has(typeOf(rows[index]))) return undefined;
  const below = rows[index + 1];
  return below?.minted === true ? undefined : idOf(below);
};

const resolveOptionOnward = then =>
  isObject(then) && then.go === 'step' && typeof then.step === 'string'
    ? then.step
    : undefined;

// ── the `clears` rule ─────────────────────────────────────────────────────────────────

/**
 * Every link a row hands control down, already resolved to a row id.
 *
 * `onward` marks the one the list itself wrote, because a backward jump taken by the onward
 * link also forgets on the row it leaves — that row IS the arrival the loop begins at.
 */
const linksOf = (row, index, rows, ifNoneOf) => {
  const out = [];
  const onward = resolveOnward(row.then, index, rows);
  if (onward !== undefined) out.push({ to: onward, onward: true });
  if (typeOf(row) === 'choose') {
    (row.options ?? []).forEach(option => {
      const to = resolveOptionOnward(option?.then);
      if (to !== undefined) out.push({ to, onward: false });
    });
  }
  if (typeOf(row) === 'choose_live') {
    const to = ifNoneOf(row, index);
    if (to !== undefined) out.push({ to, onward: false });
  }
  if (typeOf(row) === 'call') {
    const to = resolveOptionOnward(row.ifFailed);
    if (to !== undefined) out.push({ to, onward: false });
  }
  return out;
};

/**
 * What to forget on arrival at every row, worked out from the jumps back the owner chose.
 *
 * @returns {Map<string, string[]>} row id → save keys, in the order the rows that write them
 *   appear in the list. That order is not decoration: it is what lets a hand-written list and
 *   a generated one compare equal, so an unchanged task is never rewritten.
 */
const clearsForRows = (rows, ifNoneOf) => {
  const placeOf = new Map();
  rows.forEach((row, index) => {
    const id = idOf(row);
    if (typeof id === 'string' && !placeOf.has(id)) placeOf.set(id, index);
  });

  // Where each answer is written, so a forget-list can be put in list order.
  const writtenAt = new Map();
  rows.forEach((row, index) => {
    savedKeysOf(row.step).forEach(key => {
      if (key && !writtenAt.has(key)) writtenAt.set(key, index);
    });
  });

  // Every answer computed from this one, followed through connection inputs. A `call` is
  // never skipped by the walk, so its `keep` keys are not followed — it re-runs by itself.
  const computedFrom = seed => {
    const set = new Set(seed ? [seed] : []);
    // To a fixed point: an answer reached through one connection can feed the next.
    let counted = -1;
    while (set.size !== counted) {
      counted = set.size;
      rows.forEach(row => {
        if (!ANSWERING_TYPES.has(typeOf(row))) return;
        const key = answerKeyOf(row);
        if (!key || set.has(key)) return;
        if (inputKeysOf(row).some(read => set.has(read))) set.add(key);
      });
    }
    return set;
  };

  /**
   * The answer that has to be asked again for the loop to END.
   *
   * A jump back re-asks the step it lands on, and then the list runs on and reaches the same
   * jump again — so unless some CHOICE along the way is put back in the customer's hands, the
   * conversation asks one question for ever. The choice that matters is the nearest one above
   * the jump: the step whose own answer routes into the row the customer jumped from, or —
   * when nothing routes into it by a choice — that row's own answer, because then the jump is
   * one of its own options.
   *
   * Traced against the live booking task: leave the confirmation answered and "change the
   * day" asks for the day, takes the time, silently re-reads the old "change it", and asks
   * for the day again on every single turn.
   */
  const breakersFor = (fromId, fromRow) => {
    const keys = new Set();
    rows.forEach((row, index) => {
      const type = typeOf(row);
      let routes = false;
      if (type === 'choose') {
        routes = (row.options ?? []).some(
          option => resolveOptionOnward(option?.then) === fromId
        );
      }
      if (type === 'choose_live') routes = ifNoneOf(row, index) === fromId;
      if (!routes) return;
      const key = answerKeyOf(row);
      if (key) keys.add(key);
    });
    if (keys.size === 0) {
      const own = answerKeyOf(fromRow);
      if (own) keys.add(own);
    }
    return keys;
  };

  const forgets = new Map();
  rows.forEach(row => {
    const id = idOf(row);
    if (typeof id === 'string' && !forgets.has(id)) forgets.set(id, new Set());
  });

  rows.forEach((row, from) => {
    const fromId = idOf(row);
    linksOf(row, from, rows, ifNoneOf).forEach(({ to, onward }) => {
      const at = placeOf.get(to);
      // Forward is just the list running on; only a jump BACK has anything to forget.
      if (at === undefined || at > from) return;
      const set = new Set([
        ...computedFrom(answerKeyOf(rows[at])),
        ...breakersFor(fromId, row),
      ]);
      set.forEach(key => forgets.get(to)?.add(key));
      if (onward) set.forEach(key => forgets.get(fromId)?.add(key));
    });
  });

  const order = key => writtenAt.get(key) ?? Number.MAX_SAFE_INTEGER;
  const out = new Map();
  forgets.forEach((set, id) => {
    out.set(
      id,
      [...set].sort((a, b) => order(a) - order(b) || a.localeCompare(b))
    );
  });
  return out;
};

// ── spine → definition ────────────────────────────────────────────────────────────────

/** An id nothing in the list is using yet, derived from the row that asked for it. */
const mintId = (taken, base) => {
  const stem = `${String(base ?? 'step').slice(0, 26)}_none`;
  if (!taken.has(stem)) return stem;
  let n = 2;
  while (taken.has(`${stem}${n}`)) n += 1;
  return `${stem}${n}`;
};

/**
 * Mint the hand-over rows a "hand the chat to a person" outcome needs a real step for.
 *
 * Only a live list needs one: `ifNone` is REQUIRED by `taskDefinitionSchema`, and a
 * `handoff` step's `note` is `.min(1)`, so a definition that leaves either out is refused by
 * the server. Generating both is what lets the composer save a task whose owner has never
 * been asked the word `ifNone`. A call needs nothing minted — no `onFailed` IS the hand-over.
 */
const withMintedHandOvers = rows => {
  const taken = new Set(rows.map(idOf).filter(id => typeof id === 'string'));
  const minted = [];
  const ifNoneId = new Map();
  rows.forEach((row, index) => {
    if (typeOf(row) !== 'choose_live') return;
    const named = resolveOptionOnward(row.ifNone);
    if (named !== undefined) {
      ifNoneId.set(index, named);
      return;
    }
    const id = mintId(taken, idOf(row));
    taken.add(id);
    minted.push({
      step: {
        id,
        name: HAND_OVER_NAME,
        type: 'handoff',
        note: handOverNoteFor(row.step?.name),
      },
      then: CARRY_ON,
      clears: null,
      // Never reached by "carry on" (`resolveOnward`): only by the `ifNone` it was minted for.
      minted: true,
    });
    ifNoneId.set(index, id);
  });
  return { rows: [...rows, ...minted], ifNoneId };
};

/**
 * Compile the list into the graph the sidecar runs.
 *
 * TOTAL: it never throws and never returns a partial result. A row the composer has not
 * finished still compiles — the missing pieces are what `localProblems` reports and what the
 * composer refuses to send, in one place rather than two.
 */
export const spineToDefinition = spine => {
  const given = isObject(spine) && Array.isArray(spine.rows) ? spine.rows : [];
  const usable = given.filter(row => isObject(row) && isObject(row.step));
  const { rows, ifNoneId } = withMintedHandOvers(usable);
  const ifNoneOf = (row, index) => ifNoneId.get(index);
  const generated = clearsForRows(rows, ifNoneOf);

  const steps = rows.map((row, index) => {
    const step = bodyOf(row.step);
    const type = row.step.type;

    const onward = resolveOnward(row.then, index, rows);
    if (onward !== undefined) step[onwardFieldFor(type)] = onward;

    if (type === 'choose') {
      step.options = (row.options ?? []).map(option => {
        const written = optionBodyOf(option?.option ?? {});
        const to = resolveOptionOnward(option?.then);
        if (to !== undefined) written.next = to;
        return written;
      });
    }

    if (type === 'choose_live') {
      const to = ifNoneOf(row, index);
      if (to !== undefined) step.ifNone = to;
    }

    if (type === 'call') {
      const to = resolveOptionOnward(row.ifFailed);
      if (to !== undefined) step.onFailed = to;
    }

    if (Array.isArray(row.clears)) {
      step.clears = [...row.clears];
    } else {
      const worked = generated.get(row.step.id) ?? [];
      if (worked.length > 0) step.clears = worked;
    }

    return step;
  });

  return { firstStepId: idOf(rows[0]) ?? '', steps };
};

// ── a success that runs into its failure rows (P-2.2) ──────────────────────────────────

/** Whether a row hands control straight down to the row under it. */
const carriesOn = row =>
  isObject(row) &&
  !FINAL_TYPES.has(typeOf(row)) &&
  (!isObject(row.then) || row.then.go === 'on');

/**
 * The index just past row `from`'s BLOCK: that row and the rows under it, up to and including
 * the first one that stops, hands over or jumps (or the last row).
 */
export const blockEnd = (rows, from) => {
  let at = from;
  while (at < rows.length - 1 && carriesOn(rows[at])) at += 1;
  return at + 1;
};

/**
 * Whether the task, carrying on from row `from`, walks straight into row `to` (below it).
 */
export const carriesInto = (rows, from, to) => {
  if (to <= from) return false;
  for (let at = from; at < to; at += 1) if (!carriesOn(rows[at])) return false;
  return true;
};

/**
 * "Skip it when it works" (P-2.2): the owner pointed a failure arm at row `failIndex`, and the
 * row at `fromIndex` (the Call, or a live list) reaches it by carrying on when everything works.
 * The row just above the failure block then says where success goes: the first row after the
 * block, or STOP when the block runs to the end. Returns the rows unchanged when success does
 * not run into it. Pure: a new array, the input untouched.
 */
export const skipWhenWorks = (rows, fromIndex, failIndex) => {
  if (!carriesInto(rows, fromIndex, failIndex)) return rows;
  const after = blockEnd(rows, failIndex);
  const target = rows[after];
  const then = target === undefined ? STOP : goToStep(idOf(target));
  return rows.map((row, index) =>
    index === failIndex - 1 ? { ...row, then } : row
  );
};

// ── definition → spine ────────────────────────────────────────────────────────────────

/** Why a graph cannot be shown as a list. Each one names a shape the list has nowhere to put. */
export const UNREADABLE = {
  /** Not a task at all: no steps to put in a list. */
  NO_STEPS: 'no_steps',
  /** Two steps share an id, so a jump cannot say which row it means. */
  REPEATED_STEP_ID: 'repeated_step_id',
  /** `firstStepId` names no step in the list. */
  START_IS_MISSING: 'start_is_missing',
  /** The task starts somewhere other than the top of the list, and the list has no other way to say so. */
  START_IS_NOT_FIRST: 'start_is_not_first',
  /** A `call` whose onward link is `next` rather than "if it worked" — the list writes only the latter. */
  CALL_CARRIES_NEXT: 'call_carries_next',
  /** A live list with no "if nothing came back", which the list can only ever write. */
  LIVE_STEP_HAS_NO_IF_NONE: 'live_step_has_no_if_none',
};

const MESSAGES = {
  [UNREADABLE.NO_STEPS]: 'This task has no steps to put in a list.',
  [UNREADABLE.REPEATED_STEP_ID]:
    'Two steps share the id "{detail}", so a jump cannot say which one it means.',
  [UNREADABLE.START_IS_MISSING]:
    'This task starts at "{detail}", which is not one of its steps.',
  [UNREADABLE.START_IS_NOT_FIRST]:
    'This task starts at "{detail}", which is not the first step, and a list always starts where the task starts.',
  [UNREADABLE.CALL_CARRIES_NEXT]:
    'The step "{detail}" carries on through "then" rather than through "if it worked", which a list cannot write.',
  [UNREADABLE.LIVE_STEP_HAS_NO_IF_NONE]:
    'The step "{detail}" reads its choices from your system but never says what happens when nothing comes back.',
};

const reason = (code, detail, stepId) => ({
  code,
  detail,
  stepId,
  message: MESSAGES[code].replace('{detail}', detail ?? ''),
});

/**
 * Read a stored graph back as the list an owner edits.
 *
 * `unreadable` is not "this task is broken" — a broken task is still readable and its
 * problems are `localProblems`'s to report. It means the LIST cannot show this graph without
 * changing it, and the map is the honest answer.
 *
 * @returns {{spine: object|null, unreadable: boolean, reasons: object[]}}
 */
export const definitionToSpine = definition => {
  const steps = stepsOf(definition);
  const reasons = [];

  if (steps.length === 0) {
    reasons.push(reason(UNREADABLE.NO_STEPS));
    return { spine: null, unreadable: true, reasons };
  }

  const seen = new Set();
  steps.forEach(step => {
    const id = step?.id;
    if (typeof id !== 'string') return;
    if (seen.has(id)) reasons.push(reason(UNREADABLE.REPEATED_STEP_ID, id, id));
    seen.add(id);
  });

  const first = definition.firstStepId;
  if (!seen.has(first)) {
    reasons.push(reason(UNREADABLE.START_IS_MISSING, first));
  } else if (steps[0]?.id !== first) {
    reasons.push(reason(UNREADABLE.START_IS_NOT_FIRST, first, first));
  }

  steps.forEach(step => {
    if (
      step?.type === 'call' &&
      typeof step.next === 'string' &&
      step.next !== ''
    ) {
      reasons.push(reason(UNREADABLE.CALL_CARRIES_NEXT, step.id, step.id));
    }
    if (
      step?.type === 'choose_live' &&
      (typeof step.ifNone !== 'string' || step.ifNone === '')
    ) {
      reasons.push(
        reason(UNREADABLE.LIVE_STEP_HAS_NO_IF_NONE, step.id, step.id)
      );
    }
  });

  if (reasons.length > 0) return { spine: null, unreadable: true, reasons };

  const rows = steps.map((step, index) => {
    const type = step.type;
    const stored = step[onwardFieldFor(type)];
    const implied = FINAL_TYPES.has(type) ? undefined : steps[index + 1]?.id;

    let then;
    if (typeof stored !== 'string' || stored === '') {
      then = implied === undefined ? CARRY_ON : STOP;
    } else if (stored === implied) {
      then = CARRY_ON;
    } else {
      then = goToStep(stored);
    }

    const row = { step: bodyOf(step), then, clears: null };

    if (type === 'choose') {
      row.options = (Array.isArray(step.options) ? step.options : []).map(
        option => ({
          option: optionBodyOf(option),
          then:
            typeof option?.next === 'string' && option.next !== ''
              ? goToStep(option.next)
              : CARRY_ON,
        })
      );
    }
    if (type === 'choose_live') row.ifNone = goToStep(step.ifNone);
    if (type === 'call') {
      row.ifFailed =
        typeof step.onFailed === 'string' && step.onFailed !== ''
          ? goToStep(step.onFailed)
          : HAND_OVER;
    }
    return row;
  });

  // What the rule would write on its own; anything else the owner meant is carried verbatim.
  const generated = clearsForRows(rows, row => resolveOptionOnward(row.ifNone));
  rows.forEach((row, index) => {
    const stored = steps[index].clears;
    const worked = generated.get(row.step.id) ?? [];
    if (stored === undefined) {
      if (worked.length > 0) row.clears = [...worked];
      return;
    }
    const same =
      Array.isArray(stored) &&
      stored.length === worked.length &&
      stored.every((key, at) => key === worked[at]);
    if (!same) row.clears = Array.isArray(stored) ? [...stored] : stored;
  });

  return { spine: { rows }, unreadable: false, reasons: [] };
};

/**
 * Whether the list reads straight down — nothing jumps, so "Waslo works down this list" is
 * the whole truth of it.
 *
 * This, and NOT `unreadable`, is the signal for whether a task wants the map: a graph with
 * jumps in it is perfectly expressible as a list and merely harder to read as one.
 */
export const spineJumps = spine => {
  const rows = isObject(spine) && Array.isArray(spine.rows) ? spine.rows : [];
  const out = [];
  rows.forEach(row => {
    const id = idOf(row);
    if (isObject(row.then) && row.then.go === 'step') {
      out.push({ stepId: id, slot: 'then', to: row.then.step });
    }
    (row.options ?? []).forEach(option => {
      const to = resolveOptionOnward(option?.then);
      if (to !== undefined) {
        out.push({
          stepId: id,
          slot: 'option',
          optionId: option?.option?.id,
          to,
        });
      }
    });
    const none = resolveOptionOnward(row.ifNone);
    if (none !== undefined) out.push({ stepId: id, slot: 'ifNone', to: none });
    const failed = resolveOptionOnward(row.ifFailed);
    if (failed !== undefined)
      out.push({ stepId: id, slot: 'ifFailed', to: failed });
  });
  return out;
};
